import * as RadixAvatar from 'radix-ui/avatar';
import { cx } from '../../utils/cx';

export type AvatarSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

export interface AvatarProps {
  name: string;
  src?: string;
  size?: AvatarSize;
  className?: string;
  /** Set when a visible name sits next to the avatar, so screen readers do not hear it twice. */
  decorative?: boolean;
}

// Six tints, one per data-viz slot, so avatars stay distinct but on-palette.
const TINTS = 6;

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  const letters = parts.length > 1 ? [parts[0], parts[parts.length - 1]] : [parts[0]];
  return letters
    .map((p) => Array.from(p ?? '')[0] ?? '')
    .join('')
    .toLocaleUpperCase();
}

/** Stable tint per name so a child keeps "their" colour across sessions and devices. */
export function avatarTint(name: string): number {
  let hash = 0;
  for (const ch of name) hash = (hash * 31 + (ch.codePointAt(0) ?? 0)) >>> 0;
  return (hash % TINTS) + 1;
}

export function Avatar({ name, src, size = 'md', className, decorative }: AvatarProps) {
  return (
    <RadixAvatar.Root
      className={cx('kd-avatar', className)}
      data-size={size}
      data-tint={avatarTint(name)}
      role={decorative ? undefined : 'img'}
      aria-label={decorative ? undefined : name}
      aria-hidden={decorative || undefined}
    >
      {src && <RadixAvatar.Image className="kd-avatar__image" src={src} alt="" />}
      <RadixAvatar.Fallback className="kd-avatar__fallback" delayMs={src ? 300 : 0}>
        {initials(name)}
      </RadixAvatar.Fallback>
    </RadixAvatar.Root>
  );
}
